import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, CheckCircle2, Clock, X } from 'lucide-react';
import { apiService } from '../services/api.service';
import { useCashSession } from './CashSessionBar';
import {
  formatMoney,
} from '../pages/treasury/treasuryUi';

type Channel = 'CASH' | 'BANK';

type Props = {
  loan: any;
  onClose: () => void;
  onApproved: () => void;
  onError: (message: string) => void;
};

function humanize(raw: unknown): string {
  const msg = Array.isArray(raw) ? raw.join(' ') : String((raw as any)?.message ?? raw ?? '');
  const m = msg.toLowerCase();
  if (m.includes('saldo') || m.includes('fondos') || m.includes('insuficiente')) {
    return 'No hay saldo suficiente en la caja para este desembolso.';
  }
  if (m.includes('sesión') || m.includes('session') || m.includes('caja')) {
    return 'La sesión de caja no está disponible. Abrí o revisá Mi caja.';
  }
  if (m.includes('permiso') || m.includes('forbidden') || m.includes('autoriz')) {
    return 'No tenés permiso para realizar esta operación.';
  }
  if (m.includes('empresa') || m.includes('legal')) {
    return 'La cuenta o caja no corresponde a la empresa del préstamo.';
  }
  if (!msg || msg === 'undefined') return 'No se pudo completar el desembolso.';
  if (msg.length > 200) return 'No se pudo completar el desembolso. Revisá caja, sesión y datos del crédito.';
  return msg;
}

export function DisbursementApproveModal({ loan, onClose, onApproved, onError }: Props) {
  const { session, sessionId, cashRegisterId, refresh } = useCashSession();

  const [channel, setChannel] = useState<Channel>('CASH');
  const [sessionBalance, setSessionBalance] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);
  const [localError, setLocalError] = useState('');

  const [showFunding, setShowFunding] = useState(false);
  const [masters, setMasters] = useState<any[]>([]);
  const [masterBalances, setMasterBalances] = useState<Record<string, number>>({});
  const [central, setCentral] = useState<any | null>(null);
  const [centralBalance, setCentralBalance] = useState<number | null>(null);
  const [fundingFromId, setFundingFromId] = useState('');
  const [fundingAmount, setFundingAmount] = useState('');
  const [fundingBusy, setFundingBusy] = useState(false);
  const [fundingTransfer, setFundingTransfer] = useState<any | null>(null);
  const [policyThreshold, setPolicyThreshold] = useState(100000);

  const creditAmount = Number(loan.amount) || 0;
  const available = sessionBalance ?? (session?.systemBalance != null ? Number(session.systemBalance) : null);
  const after = available != null ? Math.round((available - creditAmount) * 100) / 100 : null;
  const shortfall =
    available != null && creditAmount > available
      ? Math.round((creditAmount - available) * 100) / 100
      : 0;
  const hasEnough = available != null && shortfall <= 0;
  const cajaLabel = session?.cashRegister
    ? `Caja ${session.cashRegister.number ?? session.cashRegister.code} · ${
        session.cashRegister.branch?.name ?? '—'
      }`
    : '—';

  const refreshBalance = useCallback(async () => {
    if (!sessionId) {
      setSessionBalance(null);
      return;
    }
    try {
      const bal = await apiService.getCashSessionBalance(sessionId);
      setSessionBalance(Number(bal.systemBalance ?? 0));
    } catch {
      setSessionBalance(session?.systemBalance != null ? Number(session.systemBalance) : null);
    }
  }, [sessionId, session?.systemBalance]);

  useEffect(() => {
    void refreshBalance();
    const t = window.setInterval(() => void refreshBalance(), 8000);
    return () => window.clearInterval(t);
  }, [refreshBalance]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const le = loan.legalEntityId || loan.branch?.legalEntityId;
      if (!le) return;
      try {
        const pol = await apiService.getTreasuryPolicy(le).catch(() => null);
        if (!cancelled && pol) setPolicyThreshold(Number(pol.transferApprovalThreshold ?? 100000));
      } catch {
        /* ignore */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [loan]);

  const loadFundingSources = useCallback(async () => {
    const le = loan.legalEntityId || loan.branch?.legalEntityId || session?.cashRegister?.legalEntityId;
    if (!le) return;
    const regs = await apiService.getCashRegisters(undefined, le);
    const branchId = session?.cashRegister?.branchId || loan.branchId;
    const mastersList = (regs ?? []).filter((r: any) => r.type === 'BRANCH_MASTER' && r.isActive !== false);
    // Prefer same branch first
    mastersList.sort((a: any, b: any) => {
      const as = a.branchId === branchId ? 0 : 1;
      const bs = b.branchId === branchId ? 0 : 1;
      return as - bs;
    });
    setMasters(mastersList);
    const bals: Record<string, number> = {};
    await Promise.all(
      mastersList.map(async (r: any) => {
        try {
          const b = await apiService.getCashRegisterLedgerBalance(r.id);
          bals[r.id] = Number(b.balance ?? 0);
        } catch {
          bals[r.id] = 0;
        }
      }),
    );
    setMasterBalances(bals);

    const centrals = (regs ?? []).filter(
      (r: any) => r.type === 'CENTRAL_TREASURY' && r.isActive !== false,
    );
    const c0 = centrals[0] ?? null;
    setCentral(c0);
    if (c0) {
      try {
        const b = await apiService.getCashRegisterLedgerBalance(c0.id);
        setCentralBalance(Number(b.balance ?? 0));
      } catch {
        setCentralBalance(null);
      }
    } else {
      setCentralBalance(null);
    }

    const covering = mastersList.find((r: any) => (bals[r.id] ?? 0) >= shortfall);
    const preferred =
      covering ??
      mastersList.find((r: any) => r.branchId === branchId) ??
      mastersList[0];
    if (preferred) {
      setFundingFromId(preferred.id);
      setFundingAmount(String(shortfall));
    }
  }, [loan, session, shortfall]);

  useEffect(() => {
    if (showFunding && shortfall > 0) {
      void loadFundingSources().catch((e) => setLocalError(humanize(e?.response?.data?.message || e.message)));
    }
  }, [showFunding, shortfall, loadFundingSources]);

  // Re-check funding transfer / balance when pending
  useEffect(() => {
    if (!fundingTransfer?.id || fundingTransfer.status === 'COMPLETED') return;
    const tick = async () => {
      try {
        await refresh();
        await refreshBalance();
        const list = await apiService.listTreasuryTransfers(
          loan.legalEntityId || loan.branch?.legalEntityId,
        );
        const found = (list ?? []).find((t: any) => t.id === fundingTransfer.id);
        if (found) setFundingTransfer(found);
      } catch {
        /* ignore poll errors */
      }
    };
    const id = window.setInterval(tick, 5000);
    return () => window.clearInterval(id);
  }, [fundingTransfer?.id, fundingTransfer?.status, refresh, refreshBalance, loan]);

  const selectedMasterBal = fundingFromId ? masterBalances[fundingFromId] ?? null : null;
  const fundingAmt = parseFloat(fundingAmount) || 0;
  const fundingNeedsApproval = fundingAmt > policyThreshold;
  const masterCanCover = selectedMasterBal != null && fundingAmt > 0 && selectedMasterBal >= fundingAmt - 1e-9;

  const requestFunding = async () => {
    if (!cashRegisterId || !fundingFromId || fundingAmt <= 0 || fundingBusy) return;
    if (!masterCanCover) {
      setLocalError('La caja Madre seleccionada no tiene saldo suficiente para este fondeo.');
      return;
    }
    setFundingBusy(true);
    setLocalError('');
    try {
      const res = await apiService.requestTreasuryTransfer({
        fromRegisterId: fundingFromId,
        toRegisterId: cashRegisterId,
        amount: fundingAmt,
        notes: `Fondeo desembolso préstamo ${loan.id}`,
      });
      setFundingTransfer(res);
      setShowFunding(false);
      await refresh();
      await refreshBalance();
    } catch (e: any) {
      setLocalError(humanize(e?.response?.data?.message || e.message));
    } finally {
      setFundingBusy(false);
    }
  };

  const confirmApprove = async (override?: {
    validationOverride?: boolean;
    validationOverrideReason?: string;
  }) => {
    if (busy) return;
    if (channel === 'CASH') {
      if (!sessionId || !cashRegisterId) {
        setLocalError('Abrí la caja del operador para desembolsar en efectivo.');
        return;
      }
      if (!hasEnough) {
        setLocalError('La caja no tiene fondos suficientes. Solicitá fondeo o cambiá a transferencia.');
        return;
      }
    }

    setBusy(true);
    setLocalError('');
    try {
      if (channel === 'CASH') {
        await apiService.approveLoan(loan.id, {
          ...override,
          channel: 'CASH',
          cashRegisterId,
          cashSessionId: sessionId!,
        });
      } else {
        await apiService.approveLoan(loan.id, {
          ...override,
          channel: 'BANK',
        });
      }
      onApproved();
    } catch (e: any) {
      const data = e?.response?.data;
      if (e?.response?.status === 409 && data?.requiresOverride) {
        const reason = window.prompt(
          `${data.message ?? 'Hay controles en rojo.'}\n\nMotivo para continuar (mín. 10 caracteres):`,
        );
        if (reason && reason.trim().length >= 10) {
          setBusy(false);
          await confirmApprove({
            validationOverride: true,
            validationOverrideReason: reason.trim(),
          });
          return;
        }
      }
      const msg = humanize(data?.message ?? e.message);
      setLocalError(msg);
      onError(msg);
    } finally {
      setBusy(false);
    }
  };

  const fundingPending = fundingTransfer?.status === 'PENDING';
  const fundingDone =
    fundingTransfer?.status === 'COMPLETED' || (hasEnough && fundingTransfer != null);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4 overflow-y-auto">
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full p-6 my-6 space-y-4">
        <div className="flex items-start justify-between gap-2">
          <div>
            <h3 className="text-lg font-semibold text-slate-900">Aceptar crédito</h3>
            <p className="text-sm text-slate-500 mt-0.5">
              {loan.customer?.firstName} {loan.customer?.lastName} — {formatMoney(creditAmount)}
            </p>
          </div>
          <button type="button" onClick={onClose} className="p-1 rounded hover:bg-slate-100" aria-label="Cerrar">
            <X className="h-4 w-4" />
          </button>
        </div>

        <p className="text-sm font-medium text-slate-800">¿Cómo querés desembolsar el crédito?</p>

        <div className="space-y-3">
          <label className="flex items-start gap-3 p-3 border rounded-md cursor-pointer hover:bg-gray-50">
            <input
              type="radio"
              name="approveChannel"
              className="mt-1"
              checked={channel === 'CASH'}
              onChange={() => setChannel('CASH')}
            />
            <span className="flex-1 min-w-0">
              <span className="block text-sm font-medium text-gray-900">Efectivo</span>
              <span className="block text-xs text-gray-500">
                Sale de la caja operativa con sesión abierta del operador.
              </span>
            </span>
          </label>

          <label className="flex items-start gap-3 p-3 border rounded-md cursor-pointer hover:bg-gray-50">
            <input
              type="radio"
              name="approveChannel"
              className="mt-1"
              checked={channel === 'BANK'}
              onChange={() => setChannel('BANK')}
            />
            <span className="flex-1 min-w-0">
              <span className="block text-sm font-medium text-gray-900">Transferencia bancaria</span>
              <span className="block text-xs text-gray-500">
                Queda pendiente de transferencia: después se elige la cuenta y se sube el PDF del
                comprobante.
              </span>
            </span>
          </label>
        </div>

        {channel === 'CASH' && (
          <div className="rounded-lg border bg-slate-50 p-4 space-y-2 text-sm">
            {!sessionId || !cashRegisterId ? (
              <p className="text-amber-800">
                No hay caja abierta.{' '}
                <Link to="/cash?open=1" className="underline font-medium">
                  Abrir caja
                </Link>
              </p>
            ) : (
              <>
                <div className="flex justify-between gap-2">
                  <span className="text-slate-500">Caja actual</span>
                  <span className="font-medium text-right">{cajaLabel}</span>
                </div>
                <div className="flex justify-between gap-2">
                  <span className="text-slate-500">Disponible</span>
                  <span className="font-semibold tabular-nums">
                    {available == null ? '…' : formatMoney(available)}
                  </span>
                </div>
                <div className="flex justify-between gap-2">
                  <span className="text-slate-500">Crédito</span>
                  <span className="tabular-nums">{formatMoney(creditAmount)}</span>
                </div>
                <div className="flex justify-between gap-2 border-t pt-2">
                  <span className="text-slate-500">Saldo posterior</span>
                  <span
                    className={`font-semibold tabular-nums ${
                      after != null && after < 0 ? 'text-red-700' : 'text-slate-900'
                    }`}
                  >
                    {after == null ? '—' : formatMoney(after)}
                  </span>
                </div>

                {hasEnough ? (
                  <p className="flex items-center gap-1.5 text-emerald-800 font-medium pt-1">
                    <CheckCircle2 className="h-4 w-4" /> La caja tiene fondos suficientes.
                  </p>
                ) : (
                  <div className="space-y-2 pt-1">
                    <p className="flex items-center gap-1.5 text-red-700 font-semibold">
                      <AlertTriangle className="h-4 w-4" /> Faltan {formatMoney(shortfall)}.
                    </p>
                    <div className="flex flex-wrap gap-2">
                      <button
                        type="button"
                        onClick={() => setShowFunding(true)}
                        className="px-3 py-1.5 text-sm rounded-md bg-slate-900 text-white hover:bg-slate-800"
                      >
                        Solicitar fondeo
                      </button>
                      <button
                        type="button"
                        onClick={() => setChannel('BANK')}
                        className="px-3 py-1.5 text-sm rounded-md border hover:bg-white"
                      >
                        Cambiar a transferencia bancaria
                      </button>
                    </div>
                  </div>
                )}

                {fundingPending && (
                  <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-amber-950 space-y-1">
                    <p className="font-semibold flex items-center gap-1.5">
                      <Clock className="h-4 w-4" /> Fondeo solicitado
                    </p>
                    <p className="text-xs">
                      El desembolso podrá completarse cuando la transferencia sea aprobada.
                    </p>
                    <Link to="/treasury/approvals" className="text-xs underline">
                      Ver aprobaciones
                    </Link>
                  </div>
                )}

                {fundingDone && hasEnough && (
                  <p className="flex items-center gap-1.5 text-emerald-800 font-medium">
                    <CheckCircle2 className="h-4 w-4" /> Fondos disponibles
                  </p>
                )}
              </>
            )}
          </div>
        )}

        {(localError || (!sessionId && channel === 'CASH')) && localError && (
          <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2">
            {localError}
          </p>
        )}

        <div className="flex justify-end gap-2 pt-1">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-md"
            disabled={busy}
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={() => void confirmApprove()}
            disabled={
              busy ||
              (channel === 'CASH' && (!sessionId || !cashRegisterId || !hasEnough))
            }
            className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50"
          >
            {busy
              ? channel === 'BANK'
                ? 'Marcando…'
                : 'Desembolsando…'
              : channel === 'BANK'
                ? 'Marcar transferencia'
                : channel === 'CASH' && fundingDone && hasEnough
                  ? 'Realizar desembolso'
                  : 'Confirmar desembolso'}
          </button>
        </div>
      </div>

      {showFunding && (
        <div className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-t-xl sm:rounded-xl shadow-xl max-w-md w-full p-5 space-y-3 max-h-[90vh] overflow-y-auto">
            <div className="flex items-start justify-between">
              <h4 className="font-semibold text-slate-900">Solicitar fondeo</h4>
              <button type="button" onClick={() => setShowFunding(false)} className="p-1 rounded hover:bg-slate-100">
                <X className="h-4 w-4" />
              </button>
            </div>
            <p className="text-xs text-slate-500">
              Reutiliza el movimiento de fondos de tesorería (Madre → Operativa). Central no puede
              fondear directo una operativa.
            </p>
            <dl className="text-sm space-y-1 rounded-md border bg-slate-50 px-3 py-2">
              <div className="flex justify-between gap-2">
                <dt className="text-slate-500">Caja destino</dt>
                <dd className="font-medium text-right">{cajaLabel}</dd>
              </div>
              <div className="flex justify-between gap-2">
                <dt className="text-slate-500">Saldo actual</dt>
                <dd className="tabular-nums">{available == null ? '—' : formatMoney(available)}</dd>
              </div>
              <div className="flex justify-between gap-2">
                <dt className="text-slate-500">Faltante</dt>
                <dd className="font-semibold text-red-700 tabular-nums">{formatMoney(shortfall)}</dd>
              </div>
            </dl>

            <label className="block text-sm">
              <span className="font-medium">Origen sugerido (Caja Madre)</span>
              <select
                className="mt-1 w-full border rounded-md px-3 py-2"
                value={fundingFromId}
                onChange={(e) => setFundingFromId(e.target.value)}
              >
                <option value="">Seleccioná…</option>
                {masters.map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.name || r.code}
                    {r.branch?.name ? ` · ${r.branch.name}` : ''}
                    {masterBalances[r.id] != null ? ` · ${formatMoney(masterBalances[r.id])}` : ''}
                  </option>
                ))}
              </select>
            </label>

            {masters.length === 0 && (
              <p className="text-sm text-amber-800">
                No hay caja Madre disponible. Usá transferencia bancaria o configurá la Madre de la
                sucursal.
              </p>
            )}

            {central && centralBalance != null && shortfall > 0 && !masterCanCover && (
              <p className="text-xs text-slate-600 rounded-md border px-3 py-2">
                Central tiene {formatMoney(centralBalance)}. No puede enviar directo a esta operativa.
                Primero fondeá la Madre desde Central (
                <Link to="/treasury/move?type=structural" className="text-blue-700 underline">
                  Mover fondos
                </Link>
                ) o cambiá a banco.
              </p>
            )}

            <label className="block text-sm">
              <span className="font-medium">Monto a solicitar</span>
              <input
                type="number"
                min="0.01"
                step="0.01"
                className="mt-1 w-full border rounded-md px-3 py-2"
                value={fundingAmount}
                onChange={(e) => setFundingAmount(e.target.value)}
              />
            </label>

            {fundingNeedsApproval && (
              <p className="text-xs text-amber-900 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
                Este fondeo requiere aprobación (umbral {formatMoney(policyThreshold)}). El préstamo no se
                desembolsará hasta que la transferencia esté completa y la caja tenga saldo.
              </p>
            )}

            {localError && (
              <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2">
                {localError}
              </p>
            )}

            <div className="flex justify-end gap-2 pt-1">
              <button
                type="button"
                onClick={() => setShowFunding(false)}
                className="px-3 py-2 text-sm rounded-md hover:bg-slate-100"
              >
                Cancelar
              </button>
              <button
                type="button"
                disabled={fundingBusy || !fundingFromId || !masterCanCover || fundingAmt <= 0}
                onClick={() => void requestFunding()}
                className="px-4 py-2 text-sm rounded-md bg-slate-900 text-white disabled:opacity-50"
              >
                {fundingBusy ? 'Solicitando…' : 'Confirmar solicitud'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
